/**
 * The metric list a v2 report can show, in display order: the definition's
 * static metrics with the run's dynamic metrics (e.g. one "Total amount
 * collected ({method})" column per payment method, one "Discounts ({name})"
 * column per discount) inserted after their anchor. Labels, descriptions and
 * formulas come from the API verbatim.
 */
import {
  DynamicMetricGroup,
  ReportMetricDef,
  ReportV2Definition,
} from "../../../../types/reportsV2";

const CUSTOM_PREFIX = "custom:";
/** The one placeholder in a group's label_template, e.g. "{method}". */
const LABEL_PLACEHOLDER = /\{\w+\}/;

const dynamicGroupOf = (
  def: ReportV2Definition,
  key: string,
): DynamicMetricGroup | undefined =>
  def.dynamic_metric_groups.find(
    (g) => key.startsWith(g.key_prefix) && key.length > g.key_prefix.length,
  );

/** True for a static metric key or a key of a dynamic metric group. */
export function isMetricKey(def: ReportV2Definition, key: string): boolean {
  return def.metrics.some((m) => m.key === key) || !!dynamicGroupOf(def, key);
}

/** "collected__custom:Gift Ledger" → "Gift Ledger"; "collected__card_on_file"
 *  → "Card on file"; "discount__cash discount" → "Cash discount". Only used
 *  for keys the latest run didn't describe. */
function dynamicNameOf(group: DynamicMetricGroup, key: string): string {
  const suffix = key.slice(group.key_prefix.length);
  if (suffix.startsWith(CUSTOM_PREFIX))
    return suffix.slice(CUSTOM_PREFIX.length);
  const words = suffix.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A placeholder for a dynamic metric a saved view references but the latest
 *  run didn't return (e.g. a payment method the salon no longer offers, or a
 *  discount not used in the date range). */
function placeholderMetric(
  group: DynamicMetricGroup,
  key: string,
): ReportMetricDef {
  return {
    key,
    // A function replacer: a name like "Gift $&" must not be read as a
    // replacement pattern.
    label: group.label_template.replace(LABEL_PLACEHOLDER, () =>
      dynamicNameOf(group, key),
    ),
    section: group.section,
    format: "money",
    description: "",
    formula: "",
    available: true,
    unavailable_reason: null,
    summable: true,
  };
}

/**
 * Static metrics + dynamic metrics, in display order.
 *
 * @param runMetrics metrics of the latest run (null before the first run)
 * @param referencedKeys keys the current view uses (columns, KPIs, sort), so
 *   a dynamic key missing from the run still gets a row to uncheck.
 */
export function buildMetricCatalog(
  def: ReportV2Definition,
  runMetrics: ReportMetricDef[] | null | undefined,
  referencedKeys: string[] = [],
): ReportMetricDef[] {
  const staticKeys = new Set(def.metrics.map((m) => m.key));
  const dynamic = (runMetrics ?? []).filter(
    (m) => !staticKeys.has(m.key) && !!dynamicGroupOf(def, m.key),
  );
  const seen = new Set(dynamic.map((m) => m.key));
  referencedKeys.forEach((key) => {
    const group = dynamicGroupOf(def, key);
    if (group && !seen.has(key) && !staticKeys.has(key)) {
      seen.add(key);
      dynamic.push(placeholderMetric(group, key));
    }
  });

  const out: ReportMetricDef[] = [];
  const placed = new Set<string>();
  def.metrics.forEach((metric) => {
    out.push(metric);
    def.dynamic_metric_groups
      .filter((g) => g.anchor_after === metric.key)
      .forEach((g) => {
        dynamic
          .filter((m) => m.key.startsWith(g.key_prefix))
          .forEach((m) => {
            out.push(m);
            placed.add(m.key);
          });
      });
  });
  // A group whose anchor is missing still shows its metrics, at the end.
  dynamic.filter((m) => !placed.has(m.key)).forEach((m) => out.push(m));
  return out;
}

export const metricsByKey = (
  catalog: ReportMetricDef[],
): Map<string, ReportMetricDef> => new Map(catalog.map((m) => [m.key, m]));

/**
 * Add `key` to `keys` just before the first entry that comes after it in the
 * catalog, so toggling a column on keeps catalog order without reshuffling
 * the rest of the list.
 */
export function insertInCatalogOrder(
  catalog: ReportMetricDef[],
  keys: string[],
  key: string,
): string[] {
  if (keys.includes(key)) return keys;
  const index = new Map(catalog.map((m, i) => [m.key, i]));
  const rank = (k: string) => index.get(k) ?? Number.MAX_SAFE_INTEGER;
  const at = keys.findIndex((k) => rank(k) > rank(key));
  return at === -1
    ? [...keys, key]
    : [...keys.slice(0, at), key, ...keys.slice(at)];
}
